import { z } from "zod";
import { voiceTimelineSchema } from "./voice-timeline.js";
import type { LaunchStyle } from "./launch.js";
import { generatedVideoMaxSeconds } from "./generated-video.js";
import { bookendFonts } from "./font.js";
import { matchFont, nearestFont, type ScrapedBrand } from "./brand-scrape.js";

/**
 * The look a launch video is made in.
 *
 * A template is a paragraph of art direction and a type pairing. It goes into
 * the prompt verbatim, which is the point: the structure and the editing rules
 * stay fixed while the world the film is set in changes, so a new look is a
 * new entry here rather than a new pipeline.
 *
 * Hardcoded on purpose, for now. These want to live somewhere they can be
 * added to without a deploy — a table, or a directory of files — and that move
 * is worth making once there are enough of them to make it obvious what the
 * right shape is. Until then a list is honest about what it is.
 */
export interface LaunchTemplate {
  id: string;
  name: string;
  /** Dropped into the prompt as the STYLE block. */
  style: string;
  /**
   * The faces the page is told to use.
   *
   * Every one must be a family Veyso bundles, because a generated page runs
   * with no network and its fonts are inlined from what is on disk. A template
   * naming a family we do not have is a video in Liberation Sans.
   */
  fonts: { display: string; body: string; mono: string };
  /**
   * Which hand-built composition stands in when the generated page fails.
   *
   * Declared per template rather than guessed from the id. There are more
   * looks than there are compositions — a look is a paragraph and a
   * composition is code — so every one has to name the nearest thing Veyso can
   * draw without a model. Getting it wrong is a fallback film in the wrong
   * key, which is worse than a plain one.
   */
  fallback: LaunchStyle;
  /**
   * How long this look wants to be, when it is not the usual length.
   *
   * Most of these are fifty-second films. One or two are not: a very sparse,
   * very premium teaser is a different form, and stretching it to fifty
   * seconds does not make it grander, it makes it slow. Absent means the
   * default.
   */
  seconds?: number;
  /**
   * Whether the look is drawn in the customer's colours.
   *
   * True for the looks written around "the accent colour". The others name
   * their own hexes — Neon is lime on black whatever the brand — and the
   * prompt keeps them, using the brand only for the mark and small moments.
   * A film made from somebody's website is picked from these when no look was
   * chosen, so it comes out in their colours rather than four times in ten not.
   */
  brandColours?: boolean;
}

export const launchTemplates: readonly LaunchTemplate[] = [
  {
    id: "neon",
    name: "Neon",
    fallback: "neon",
    fonts: { display: "Anton", body: "Inter", mono: "JetBrains Mono" },
    style:
      "Pure black background, lime neon green #C8FF3D as the primary glow colour for text and interface outlines, with warm orange-red ambient light rising from the bottom edge and breathing slowly. Pain words in red #FF4D4D, solution words in green. Animated stat counters, glowing connector lines between cards, and a scrubbing timeline with glowing nodes. Soft bloom on every highlight. Heavy condensed uppercase headlines and a vignette pulling the eye to the centre. High contrast, kinetic, confident.",
  },
  {
    id: "soft",
    name: "Soft",
    fallback: "soft",
    fonts: { display: "Outfit", body: "Figtree", mono: "IBM Plex Mono" },
    style:
      "Warm off-white #FAF6F1 with large blurred pastel gradient orbs — peach, mint and lavender — drifting slowly on mismatched orbits, plus small floating translucent squares. Glassmorphism pill buttons and cards with soft shadows and a gentle 3D perspective tilt. Teal #0F9E8E is the accent; terracotta #C25F45 marks the problem. Real application icons to show the problem. Benefits appear as speech-bubble tags. No glow anywhere — on a light ground it reads as a printing fault. Rounded geometric headlines set tight. Calm, airy and optimistic.",
  },
  {
    id: "terminal",
    name: "Terminal",
    fallback: "terminal",
    fonts: { display: "JetBrains Mono", body: "IBM Plex Mono", mono: "JetBrains Mono" },
    style:
      "Near-black maroon #150A0D with a glowing coral-pink wave #FF6B4A sweeping across the frame on a slow diagonal, over a faint grid. Monospace typewriter headlines with a blinking cursor, set tight so they read as type rather than as code. Dashboards slide past at an angle with glowing rim light. Overlapping glowing circles as a concept visual. Benefits type into outlined checkbox pills that tick one by one, a green #5FD68C marking each as it passes. Ends on an icon logo reveal with a tagline. Mysterious, techy and confident.",
  },
  {
    id: "studio",
    name: "Studio",
    fallback: "soft",
    fonts: { display: "Space Grotesk", body: "Space Grotesk", mono: "JetBrains Mono" },
    style:
      "Cool off-white #F5F6FA with large soft radial lavender, mint and peach orbs drifting slowly, plus a subtle film grain. Ink #15141C, muted text #6D6C7C, accent violet #5B45FF, pain red #F2464B. Violet is only ever for the product and the parts that are kept; red is only for pain and the parts that get cut. White cards with 26px radius, a soft violet-tinted shadow and a 3D perspective tilt, floating on a gentle sine tilt while on screen. Monospace for timecodes, file names and statistics. Calm, airy and confident.",
  },

  /* ── Added as a set, each one a whole look rather than a palette ─────── */

  {
    id: "warm-editorial",
    brandColours: true,
    name: "Warm editorial",
    fallback: "soft",
    fonts: { display: "Fraunces", body: "DM Sans", mono: "IBM Plex Mono" },
    style:
      "Cream background with huge abstract arcs in the accent colour sweeping behind the interface, cropped by the frame so they read as shapes rather than as decoration. Round-framed portraits joined by dotted lines to a central node icon. Testimonial cards fanned out in a shallow 3D stack. Feature icons orbiting a single action button. One line of type repeated down the screen in fading echoes. A warm serif for headlines, generous leading, nothing sharp-edged. Friendly, confident and human — the look of a company that would answer the phone.",
  },
  {
    id: "cinematic-minimal",
    brandColours: true,
    name: "Cinematic minimal",
    fallback: "neon",
    // A teaser, not a film. Fifty seconds of this is fifty seconds of nothing
    // happening slowly; the restraint is the whole effect and it does not
    // stretch.
    seconds: 16,
    fonts: { display: "Manrope", body: "Inter", mono: "JetBrains Mono" },
    style:
      "Opens on off-white with a single glowing icon dead centre, holds, then flips hard to black. One enormous hero number built from drifting particles that assemble and settle. Text types itself into a chat input one character at a time, an 'Analysing' chip pulses with a soft halo, and the answer resolves beneath it. Almost nothing on screen at any moment and a great deal of empty space. Extremely sparse, extremely premium, every element given room and time.",
  },
  {
    id: "playful-gradient",
    brandColours: true,
    name: "Playful gradient",
    fallback: "soft",
    fonts: { display: "Outfit", body: "Figtree", mono: "Space Mono" },
    style:
      "White at the top fading into a saturated gradient of the accent colour at the bottom, with thin concentric rings pulsing outward behind each key moment. A cartoon pointing-hand cursor that moves with weight and overshoot. Close-up crops of form fields, sliders and dropdowns being filled in, framed tight so the interface reads as texture. Statistics land under a sharp diagonal ribbon. Small icons burst outward after a decisive click. Rounded geometric type, bouncy easing, nothing held still for long. Energetic and fun.",
  },
  {
    id: "dark-premium",
    brandColours: true,
    name: "Dark premium",
    fallback: "neon",
    fonts: { display: "Archivo", body: "Manrope", mono: "IBM Plex Mono" },
    style:
      "Near-black background with a single light leak in the accent colour bleeding in from one corner and never moving far. Dark-mode interface panels drifting slowly in perspective, captions anchored low and left in small type. A notification card drops in with weight for the support moment. Application icons orbit a centre point for social proof. Statistics slide up one after another, each pushing the last away. Ends on an enormous glowing wordmark on black. Tight grotesque headlines, restrained motion, serious and high-end.",
  },
  {
    id: "red-to-green",
    brandColours: true,
    name: "Red to green",
    fallback: "soft",
    fonts: { display: "Bitter", body: "Work Sans", mono: "JetBrains Mono" },
    style:
      "Frosted white background with dozens of document cards floating at different depths behind a heavy blur, so the field reads as paperwork without any of it being legible. The problem arrives as red and yellow sticky-note questions pinned over them. A hand-drawn doodle loop circles the question that matters. The old way glows red; then dashed data lines flow across and converge into a glowing hub in the accent colour. Figures count up on cards as they settle. A slab serif for headlines. A clear before-and-after, told in colour.",
  },
  {
    id: "kinetic-type",
    brandColours: true,
    name: "Kinetic type",
    fallback: "soft",
    fonts: { display: "Anton", body: "Inter", mono: "Space Mono" },
    style:
      "Mostly enormous bold typography on a pale tinted background. Words slam into place with heavy motion blur and stop dead. Three times the frame floods edge to edge with solid accent colour — for the logo, for the turn, and for the closing call to action — and the type reverses out of it. Stacked semi-transparent interface layers with gradient shadows tinted in the accent colour between them. Single huge statistic cards, one number each, held for a beat and replaced. Punchy and fast, cut on the beat.",
  },
];

/**
 * A template, at random.
 *
 * Random rather than chosen. Asking the model which look suits the product
 * sounds better than it is: it costs a judgement call on thin evidence, it
 * makes two accounts with similar sites get the same film, and it gives the
 * model one more thing to get wrong in a response that already has to be
 * valid. A roll of the dice is honest, and every template is meant to be good
 * enough that landing on any of them is a fine outcome.
 *
 * The generator is injected so a test can pin the result; nothing else should
 * pass it.
 */
export function pickLaunchTemplate(
  random: () => number = Math.random,
  options: { brandColours?: boolean } = {},
): LaunchTemplate {
  const from = options.brandColours ? launchTemplates.filter((one) => one.brandColours) : launchTemplates;
  const index = Math.floor(random() * from.length);
  // A generator that returns exactly 1, or drifts a hair above it, would index
  // off the end and hand back undefined — which reaches the prompt as the word
  // "undefined" rather than as an error.
  return from[Math.min(Math.max(index, 0), from.length - 1)];
}

/**
 * The look with the site's own typeface for its headlines and text.
 *
 * Only a family Veyso bundles, since the page's fonts are inlined from disk;
 * the API sends one only when it is. The look's monospace stays, for the
 * timecodes and figures it is chosen for.
 */
export function withSiteFont(template: LaunchTemplate, font: string | undefined): LaunchTemplate {
  if (!font) return template;
  return { ...template, fonts: { ...template.fonts, display: font, body: font } };
}

/**
 * The customer's mark, in a form a generated page can actually draw.
 *
 * A URL is no good: the page runs with no network at an opaque origin, so a
 * logo it has to fetch is a logo it does not have. Either the bytes travel
 * with the page or there is no image and the name is set as type.
 */
export type LaunchLogo =
  | { kind: "image"; dataUri: string }
  | {
      kind: "wordmark";
      text: string;
      /** A family Veyso can inline. Never the site's own, unless we have it. */
      family: string;
      /** False when the site's typeface is not one we have, and this is the nearest. */
      matched: boolean;
      /** What the site actually used, kept so the substitution can be admitted. */
      wanted: string | null;
    };

/** What a logo may be. SVG first: it is small and it scales to any frame. */
export const logoMimeTypes = ["image/svg+xml", "image/png", "image/webp", "image/jpeg"] as const;

/**
 * 256KB.
 *
 * A logo larger than this is a hero image someone linked as a logo, and it
 * travels inside every copy of the page.
 */
export const maxLogoBytes = 256 * 1024;

/**
 * The smallest logo worth putting in a 1920-pixel frame.
 *
 * Vercel's homepage offers `apple-touch-icon-57x57.png`, which is a real logo
 * and useless here — blown up to any size a video would use it is a blur.
 */
export const minLogoPixels = 96;

/**
 * A PNG's dimensions, from its header.
 *
 * Eight bytes of signature, then the IHDR chunk: length, type, width, height,
 * all big-endian. It is the first chunk in every valid PNG, so this reads a
 * fixed offset rather than walking the file.
 *
 * Only PNG. It is far and away the most common format an icon arrives in, and
 * the alternative for the rest — a byte-count heuristic — is at least honest
 * about being a guess.
 */
export function pngSize(bytes: Uint8Array): { width: number; height: number } | null {
  if (bytes.length < 24) return null;
  const signature = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
  if (signature.some((byte, index) => bytes[index] !== byte)) return null;
  if (String.fromCharCode(bytes[12], bytes[13], bytes[14], bytes[15]) !== "IHDR") return null;
  const read = (at: number) => (bytes[at] << 24) | (bytes[at + 1] << 16) | (bytes[at + 2] << 8) | bytes[at + 3];
  const width = read(16);
  const height = read(20);
  if (width <= 0 || height <= 0) return null;
  return { width, height };
}

/**
 * Whether an image is big enough to use.
 *
 * SVG is exempt and always passes: it scales, so its byte count says nothing
 * about how it will look. A PNG is measured. Anything else falls back to a
 * byte floor, which is crude but catches the tracking pixel and the 16px
 * favicon, which is what this is for.
 */
export function isLogoBigEnough(bytes: Uint8Array, mimeType: string): boolean {
  if (mimeType === "image/svg+xml") return true;
  const size = pngSize(bytes);
  if (size) return Math.min(size.width, size.height) >= minLogoPixels;
  return bytes.length >= 2048;
}

export function isLogoMimeType(value: string | null | undefined): boolean {
  if (!value) return false;
  const bare = value.split(";")[0].trim().toLowerCase();
  return (logoMimeTypes as readonly string[]).includes(bare);
}

const BASE64 = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";

/**
 * Base64 without `Buffer`.
 *
 * This module is reachable from the web app, and `Buffer` is Node's. A bundler
 * either shims it -- adding weight to every page for a function the browser
 * never calls -- or lets it through to fail at runtime.
 */
function toBase64(bytes: Uint8Array): string {
  let out = "";
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i];
    const b = bytes[i + 1];
    const c = bytes[i + 2];
    out += BASE64[a >> 2];
    out += BASE64[((a & 3) << 4) | ((b ?? 0) >> 4)];
    out += b === undefined ? "=" : BASE64[((b & 15) << 2) | ((c ?? 0) >> 6)];
    out += c === undefined ? "=" : BASE64[c & 63];
  }
  return out;
}

export function logoDataUri(bytes: Uint8Array, mimeType: string): string {
  const bare = mimeType.split(";")[0].trim().toLowerCase();
  return `data:${bare};base64,${toBase64(bytes)}`;
}

/**
 * The name set as type, when there is no image to use.
 *
 * This is the case the site's own typeface actually matters for. A wordmark in
 * the wrong face is not a neutral fallback — it is somebody else's brand — so
 * the family is matched against what we can render and the substitution is
 * reported rather than hidden.
 *
 * `matchFont` is loose on purpose, because sites write "Inter Tight" and
 * "Inter var". `nearestFont` then answers by category, so a site set in a
 * serif we do not have gets a serif rather than Inter.
 */
export function wordmarkFor(
  brand: Pick<ScrapedBrand, "name" | "titleFont" | "bodyFont">,
  fallbackName: string,
): LaunchLogo {
  const wanted = brand.titleFont ?? brand.bodyFont ?? null;
  const matched = wanted ? matchFont(wanted) !== null : false;
  const family = (wanted ? nearestFont(wanted) : null) ?? bookendFonts[0].family;
  return {
    kind: "wordmark",
    text: (brand.name ?? fallbackName).trim(),
    family,
    matched,
    wanted,
  };
}

/**
 * Somebody else's SVG, made safe to put inside our page.
 *
 * A logo is markup a customer's server chose, and it ends up embedded in a
 * document Veyso renders. Drawing it through `<img src="data:image/svg+xml…">`
 * is already most of the defence — an image-sourced SVG runs no script and
 * loads no external reference, by specification — but the generated page is
 * itself model-written, and a page that inlines the markup instead of using an
 * `<img>` would undo that silently. Stripping the dangerous parts first means
 * the safety does not depend on the model having read the instructions.
 *
 * Deliberately conservative and deliberately not a parser. It removes whole
 * elements that can execute or embed, every inline event handler, and every
 * reference that points anywhere but inside the document. A regex cannot be a
 * complete HTML sanitiser and this one does not pretend to be; it is the
 * second of two layers, and the first one is the `<img>`.
 */
export function sanitizeSvg(markup: string): string {
  return (
    markup
      // Elements whose whole purpose is to run or fetch something.
      .replace(/<\s*(script|foreignObject|iframe|object|embed|audio|video|animate|set)\b[\s\S]*?<\s*\/\s*\1\s*>/gi, "")
      // The same, self-closed.
      .replace(/<\s*(script|foreignObject|iframe|object|embed|audio|video|animate|set)\b[^>]*\/\s*>/gi, "")
      // An unclosed <script>, which a browser does not recover from so much
      // as surrender to: everything after it becomes script content. Dropping
      // the tag alone would leave that content behind as text, so the safe
      // reading of the markup is the browser's — discard to the end.
      .replace(/<\s*script\b[^>]*>[\s\S]*$/i, "")
      // The rest cannot execute on their own; losing the tag is enough.
      .replace(/<\s*(foreignObject|iframe|object|embed)\b[^>]*>/gi, "")
      // Inline handlers: onload, onclick, onmouseover and the rest.
      .replace(/\son[a-z]+\s*=\s*"[^"]*"/gi, " ")
      .replace(/\son[a-z]+\s*=\s*'[^']*'/gi, " ")
      .replace(/\son[a-z]+\s*=\s*[^\s>]+/gi, " ")
      // References out of the document. A fragment is fine — that is how an
      // SVG points at its own gradients — and so is an inline image.
      .replace(/\s(?:xlink:)?href\s*=\s*"(?!#|data:image\/)[^"]*"/gi, " ")
      .replace(/\s(?:xlink:)?href\s*=\s*'(?!#|data:image\/)[^']*'/gi, " ")
      // `javascript:` anywhere at all, whatever attribute carried it.
      .replace(/javascript\s*:/gi, "")
  );
}

/**
 * What a launch job needs to know, carried on the job itself.
 *
 * Self-contained on purpose. The scrape happens once, at signup, while we are
 * already on the customer's site — re-fetching it later in the worker would be
 * a second request to somebody else's server for data we already had, and
 * would fail differently if their site changed in between.
 *
 * Shared between the route that writes it and the pipeline that reads it, so
 * the two cannot disagree about the shape.
 */
const launchJobPayloadShape = z.object({
  /**
   * The product's site, or empty for one described in words.
   *
   * It was required, and the described path has always written "" — so every
   * film made from a description failed here in the worker, before any of it
   * ran, and could not be tried again either.
   */
  website: z.string().default(""),
  name: z.string().min(1),
  /** Stripped page prose. Empty when the page was a JavaScript shell. */
  pageText: z.string().default(""),
  /**
   * Where `pageText` came from.
   *
   * A scraped page is filtered for being a JavaScript shell; a brief somebody
   * wrote or accepted is not, however short. Defaults to "site" for a job
   * queued before this existed; see `launchJobPayloadSchema` for how that
   * default is chosen.
   */
  pageSource: z.enum(["site", "described"]).default("site"),
  logo: z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("image"), dataUri: z.string() }),
    z.object({
      kind: z.literal("wordmark"),
      text: z.string(),
      family: z.string(),
      matched: z.boolean(),
      wanted: z.string().nullable(),
    }),
  ]),
  /** Pins the look. Only a test or a deliberate re-run sets this. */
  templateId: z.string().optional(),
  /**
   * Whether Veyso is paying for this one.
   *
   * True only for the film a brand-new account is given on the way in — that
   * is a gift, and charging somebody for a video they did not ask for is a
   * strange way to open a relationship. One made deliberately is a video like
   * any other and counts like one.
   *
   * Defaults to false so the exception has to be asked for. A gift that has to
   * be declared is a gift somebody chose to give; a charge that has to be
   * declared is revenue nobody remembers to collect.
   */
  free: z.boolean().default(false),
  /**
   * The colours the film is drawn in.
   *
   * Resolved once, where the kit and the scrape are both to hand, rather than
   * read again in the worker — the same reason the logo and the page text
   * travel on the job. Optional because a job queued before this existed has
   * none, and a film without them is the behaviour that shipped.
   */
  colors: z
    .object({
      accent: z.string().max(32),
      background: z.string().max(32),
      text: z.string().max(32),
      /** A second highlight; absent on a job queued before it was sent. */
      accentAlt: z.string().max(32).optional(),
    })
    .optional(),
  /**
   * The website's typeface, for the film's headlines and text.
   *
   * Set only for a film made from a website whose face Veyso bundles. Carried
   * on every later version of the film, as the colours are.
   */
  siteFont: z.string().max(80).optional(),
  /**
   * How long the film should be, when somebody said.
   *
   * Overrides the template's own opinion, which in turn overrides the default.
   * Three levels sounds like two too many until you have a teaser style that
   * wants sixteen seconds and somebody who wants thirty of it.
   */
  seconds: z.number().int().min(10).max(generatedVideoMaxSeconds).optional(),
  /**
   * The voice-over, as somebody wrote it.
   *
   * Present only when a person edited the script and asked for the film again.
   * It is read as written — not rewritten, not trimmed — because the reason to
   * edit a script is to say exactly these words. Absent, the model writes one.
   */
  script: z.string().trim().min(1).max(4000).optional(),
  /** What the customer asked for in the chat; the worker turns it into a script and directions. */
  message: z.string().trim().min(1).max(2000).optional(),
  /** Directions for the picture, from the revision call. */
  direction: z.string().trim().max(2000).optional(),
  /** The script of the version this one is made from, for the revision call to start from. */
  baseScript: z.string().trim().max(4000).optional(),
});

/**
 * The job payload, with `pageSource` filled in for one queued without it.
 *
 * Read from the shape rather than defaulted blindly: before pageSource a site
 * job could never have an empty website, so an empty website with no
 * pageSource is a described one. Defaulting it to "site" would let a retry
 * through and then drop the brief as a JavaScript shell.
 */
export const launchJobPayloadSchema = z.preprocess((raw) => {
  if (typeof raw !== "object" || raw === null || "pageSource" in raw) return raw;
  const website = (raw as { website?: unknown }).website;
  return website === "" ? { ...raw, pageSource: "described" } : raw;
}, launchJobPayloadShape);

export type LaunchJobPayload = z.infer<typeof launchJobPayloadSchema>;

/** One model call's instructions, as sent. */
const sentPromptSchema = z.object({ system: z.string(), user: z.string() });

/**
 * What a launch job records about what it made.
 *
 * Kept on the job's `result`, apart from its `payload`: the payload is what the
 * job was asked to do and is read back to make the film again, so output stored
 * there would quietly become the next run's input.
 */
export const launchJobResultSchema = z.object({
  narration: z
    .object({
      script: z.string(),
      /** True when a person wrote it, rather than the model. */
      edited: z.boolean(),
      /** The word limit for this length and voice — what an edit should stay under. */
      budget: z.number().int(),
      modelId: z.string(),
      /** When each line was spoken; null for a film voiced without timings. */
      timeline: voiceTimelineSchema.nullable().default(null),
    })
    .nullable()
    .default(null),
  /** Why there is no voice, when there is none. */
  silentBecause: z.string().nullable().default(null),
  /**
   * The directions this film was actually made with.
   *
   * Versions are cumulative: the next revision starts from these, so a darker
   * film stays dark when the following request is only about the close.
   */
  direction: z.string().nullable().default(null),
  /** The look actually used, which the revision call may have changed from the payload's. */
  templateId: z.string().nullable().default(null),
  prompts: z
    .object({
      narration: sentPromptSchema.nullable().default(null),
      page: sentPromptSchema.nullable().default(null),
      brief: sentPromptSchema.nullable().default(null),
    })
    .default({}),
});

export type LaunchJobResult = z.infer<typeof launchJobResultSchema>;

/**
 * A data URI taken apart.
 *
 * Logos arrive as one from the scrape and from the browser's own file reader,
 * and both ends need the bytes rather than the string — to write a file, to
 * measure a PNG, to check it is an image at all. Returns null for anything
 * that is not a data URI, which includes the empty string a cleared field
 * leaves behind.
 */
export function parseDataUri(value: string): { mimeType: string; bytes: Uint8Array } | null {
  const match = /^data:([^;,]+)(;base64)?,(.*)$/s.exec(value.trim());
  if (!match) return null;
  const [, mimeType, isBase64, body] = match;

  try {
    if (!isBase64) return { mimeType, bytes: new TextEncoder().encode(decodeURIComponent(body)) };
    const binary = atob(body);
    const bytes = new Uint8Array(binary.length);
    for (let at = 0; at < binary.length; at += 1) bytes[at] = binary.charCodeAt(at);
    return { mimeType, bytes };
  } catch {
    // Truncated base64, or a body that is not what the header claims.
    return null;
  }
}

/** The file extension a logo of this type should be written with. */
export function logoExtension(mimeType: string): string {
  switch (mimeType.split(";")[0].trim().toLowerCase()) {
    case "image/svg+xml":
      return "svg";
    case "image/png":
      return "png";
    case "image/webp":
      return "webp";
    case "image/jpeg":
      return "jpg";
    default:
      return "bin";
  }
}
